import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, exhaustMap, map, mergeMap, switchMap, takeUntil, withLatestFrom} from 'rxjs/operators';
import {of} from 'rxjs';
import {Store} from '@ngrx/store';
import * as contactsActions from './contacts-actions';
import * as checkoutActions from '../checkout/checkout-actions';
import {ContactService} from '@app/marketplace/checkout/_services/contact.service';
import * as ordersActions from '@app/marketplace/orders/state/order-list/order-list-actions';
import * as routerSelectors from '@app/marketplace/route/router-state.selectors';
import * as locationSelectors from '@app/marketplace/locations/state/locations-selectors';
import {ResponseBaseModel, ResponseModel} from '@app/marketplace/core/models/response.model';
import {PutContactModel} from '@app/marketplace/checkout/_models/contact.model';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';

@Injectable()
export class ContactsEffects {
  constructor(private actions$: Actions, private service: ContactService, private store: Store<any>) {
  }

  loadContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contactsActions.LOADING_CONTACTS),
      withLatestFrom(
        this.store.select(routerSelectors.getMergedRoute),
        (action: any, router: any) => {
          return {
            payload: action.payload,
            params: router.params,
          };
        }
      ),
      switchMap((action: any) => {
        const payload = {
          locations: action.payload?.customLocation ? action.payload?.locations || [] : [],
          locationType: action.payload?.customLocation ? action.payload?.locationType || 0 : 0,
        };
        return this.service.ListContacts({...action.payload, locations: payload.locations, locationType: payload.locationType}).pipe(
          takeUntil(
            this.actions$.pipe(ofType(contactsActions.CANCEL_CONTACTS_REQUEST))
          ),
          map((data: any) => {
            data.count = data.data.length;
            return {type: contactsActions.LIST_CONTACTS, data};
          }),
          catchError((error) => of({type: contactsActions.ERROR_CONTACTS_REQUEST}))
        );
      })
    )
  );
  addContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contactsActions.ADDING_CONTACT),
      withLatestFrom(
        this.store.select(routerSelectors.getMergedRoute),
        (action: any, router: any) => {
          return {
            type: action.type,
            payload: action.payload,
            data: action.data,
            path: router.url
          };
        }
      ),
      switchMap((action: any) => {
        return this.service.AddContact(action.data).pipe(
          takeUntil(
            this.actions$.pipe(ofType(contactsActions.CANCEL_CONTACTS_REQUEST))
          ),
          mergeMap((data: ResponseBaseModel) => {
            const list = [];
            if (action.data.isDefault) {
              list.push({type: contactsActions.ADDED_CONTACT, data});
              if (action.path == '/market/revisar-completar-pedido') {
                list.push({type: checkoutActions.LOADING_CHECKOUT, payload: { contactId: '' }});
              }
            } else {
              list.push(contactsActions.LoadingContacts({payload: action.payload}));
            }
              return list;
          }),
          catchError((error) => of({type: contactsActions.ERROR_CONTACTS_REQUEST}))
        );
      })
    )
  );
  updateContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contactsActions.SELECTING_DEFAULT_CONTACT, contactsActions.UPDATING_CONTACT),
      withLatestFrom(
        this.store.select(routerSelectors.getMergedRoute),
        (action: any, router: any) => {
          return {
            type: action.type,
            payload: action.payload,
            data: action.data,
            path: router.url
          };
        }
      ),
      switchMap((action: any) => {
        return this.service.UpdateContact(action.data).pipe(
          takeUntil(
            this.actions$.pipe(ofType(contactsActions.CANCEL_CONTACTS_REQUEST))
          ),
          mergeMap((data: ResponseBaseModel) => {
            if (action.type == contactsActions.SELECTING_DEFAULT_CONTACT) {
              const list = [];
              list.push({type: contactsActions.SELECTED_DEFAULT_CONTACT, data, code: action.data.code });
              if (action.path == '/market/revisar-completar-pedido') {
                list.push({type: checkoutActions.LOADING_CHECKOUT, payload: { contactId: action.data.code }});
              }
              return list;
            } else {
              const list = [];
              list.push(contactsActions.LoadingContacts({payload: action.payload}));
              if (action.data.isDefault) {
                if (action.path == '/market/revisar-completar-pedido') {
                  list.push({type: checkoutActions.LOADING_CHECKOUT, payload: { contactId: action.data.code }});
                }
              }
              return list;
            }
          }),
          catchError((error) => of({type: contactsActions.ERROR_CONTACTS_REQUEST}))
        );
      })
    )
  );

  deleteContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contactsActions.REMOVING_CONTACT),
      withLatestFrom(
        this.store.select(routerSelectors.getMergedRoute),
        (action: any, router: any) => {
          return {
            type: action.type,
            payload: action.payload,
            codes: action.codes,
            path: router.url
          };
        }
      ),
      switchMap((action: any) => {
        return this.service.RemoveContact(action.codes.map(x => x.code)).pipe(
          takeUntil(
            this.actions$.pipe(ofType(contactsActions.CANCEL_CONTACTS_REQUEST))
          ),
          mergeMap((data: ResponseBaseModel) => {
            if (data.success) {
              const list = [];
              list.push(contactsActions.LoadingContacts({payload: action.payload}));
              if (action.codes.find(x => x.isDefault)) {
                if (action.path == '/market/revisar-completar-pedido') {
                  list.push({type: checkoutActions.LOADING_CHECKOUT, payload: { contactId: '' }});
                }
              }
              return list;
            }
          }),
          catchError((error) => of({type: contactsActions.ERROR_CONTACTS_REQUEST}))
        );
      })
    )
  );
}
